<template>
  <div class="geolocation-settings">
    <div class="settings-content">
      <!-- Geolocation Configuration Section -->
      <div class="config-section">
        <div class="section-header">
          <div class="header-icon">
            <i class="fas fa-map-marker-alt"></i>
          </div>
          <div class="header-text">
            <h3 class="section-title">Configuração de Geolocalização</h3>
            <p class="section-subtitle">
              Configure as permissões de localização para este usuário
            </p>
          </div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <div class="setting-icon">
              <i class="fas fa-location-arrow"></i>
            </div>
            <div class="setting-details">
              <h4 class="setting-title">Ativar Geolocalização do Usuário</h4>
              <p class="setting-description">
                Permitir captura de localização em tempo real durante o uso do
                sistema
              </p>
            </div>
          </div>

          <div class="setting-control">
            <div class="switch-container">
              <label class="switch-modern">
                <input
                  type="checkbox"
                  v-model="check_geolocation"
                  @change="updateUseGeolocation"
                  :disabled="loading"
                />
                <span class="slider-modern">
                  <span class="slider-thumb">
                    <i class="fas fa-check thumb-icon"></i>
                  </span>
                </span>
              </label>
            </div>

            <div class="status-badge">
              <span
                :class="[
                  'badge',
                  check_geolocation ? 'badge-active' : 'badge-inactive',
                ]"
              >
                <i
                  :class="[
                    'fas',
                    check_geolocation ? 'fa-check' : 'fa-times',
                    'badge-icon',
                  ]"
                ></i>
                {{ check_geolocation ? "ATIVO" : "INATIVO" }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Duty History Section -->
      <div v-if="permission('read', 'duty')" class="history-section">
        <div class="section-header">
          <div class="header-icon">
            <i class="fas fa-history"></i>
          </div>
          <div class="header-text">
            <h3 class="section-title">Histórico de Plantões</h3>
            <p class="section-subtitle">
              Visualize o histórico de localização dos plantões realizados
            </p>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters-container">
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-calendar-plus mr-2"></i>
                Data Início
              </label>
              <div class="date-input-container">
                <input
                  type="date"
                  v-model="date_init"
                  class="date-input"
                  :disabled="loading"
                />
                <i class="fas fa-calendar-alt date-icon"></i>
              </div>
            </div>

            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-calendar-minus mr-2"></i>
                Data Fim
              </label>
              <div class="date-input-container">
                <input
                  type="date"
                  v-model="date_end"
                  class="date-input"
                  :disabled="loading"
                />
                <i class="fas fa-calendar-alt date-icon"></i>
              </div>
            </div>

            <div class="filter-group filter-button-group">
              <v-btn
                :disabled="loading"
                @click="getHistoryDuty"
                class="filter-btn"
              >
                <i class="fas fa-filter mr-2"></i>
                {{ loading ? "Filtrando..." : "Filtrar" }}
                <div v-if="loading" class="btn-spinner">
                  <i class="fas fa-spinner fa-spin"></i>
                </div>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <ErrorsMessages :errors="errors" />

        <!-- Data Table -->
        <div class="table-container">
          <div class="table-header">
            <h4 class="table-title">
              <i class="fas fa-table mr-2"></i>
              Registros de Plantões
            </h4>
            <div class="table-info">
              <span class="record-count">
                {{ data.length }} registro{{ data.length !== 1 ? "s" : "" }}
              </span>
            </div>
          </div>

          <div class="table-wrapper">
            <v-data-table
              class="modern-table"
              dense
              :headers="headers"
              :items="data"
              item-key="id"
              :loading="loading"
              loading-text="Carregando registros..."
              no-data-text="Nenhum registro encontrado para o período selecionado"
              :footer-props="{
                'items-per-page-options': [25, 50, 100],
                'items-per-page-text': 'Registros por página:',
                'page-text': '{0}-{1} de {2}',
              }"
            >
              <template v-slot:[`item.created_at`]="{ item }">
                <div class="date-cell">
                  <div class="date-icon-container start-icon">
                    <i class="fas fa-play"></i>
                  </div>
                  <div class="date-info">
                    <span class="date-primary">{{
                      formatDate(item.created_at, false)
                    }}</span>
                    <span class="date-secondary">{{
                      formatTime(item.created_at)
                    }}</span>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.updated_at`]="{ item }">
                <div class="date-cell">
                  <div class="date-icon-container end-icon">
                    <i class="fas fa-stop"></i>
                  </div>
                  <div class="date-info">
                    <span class="date-primary">{{
                      formatDate(item.updated_at, false)
                    }}</span>
                    <span class="date-secondary">{{
                      formatTime(item.updated_at)
                    }}</span>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.init_location`]="{ item }">
                <div class="location-cell">
                  <LinkVerLocalizacaoRegistro
                    :item="{
                      id: `${item.id}_init`,
                      latitude: item.init_latitude,
                      longitude: item.init_longitude,
                    }"
                  />
                </div>
              </template>

              <template v-slot:[`item.end_location`]="{ item }">
                <div class="location-cell">
                  <LinkVerLocalizacaoRegistro
                    :item="{
                      id: `${item.id}_end`,
                      latitude: item.end_latitude,
                      longitude: item.end_longitude,
                    }"
                  />
                </div>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>

      <!-- Loading Overlay -->
      <div v-if="loading && !initialized" class="loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <p class="loading-text">Carregando configurações...</p>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";
import { formatDate } from "../../lib/format";
import api from "../../services/api";
import toast from "../../lib/ToastNotification";
import LinkVerLocalizacaoRegistro from "../Components/LinkVerLocalizacaoRegistro";
import moment from "moment";
import permission from "../../lib/permission";

export default {
  name: "Geolocation",

  components: {
    ErrorsMessages,
    LinkVerLocalizacaoRegistro,
  },

  props: {
    user_id: {
      type: [Number, String],
    },

    geolocation: {
      type: [Number, String],
    },
  },

  data() {
    return {
      date_init: moment().startOf("month").format("YYYY-MM-DD"),
      date_end: moment().format("YYYY-MM-DD"),
      errors: [],
      loading: false,
      check_geolocation: this.geolocation == 1,
      initialized: false,
      data: [],
      headers: [
        {
          text: "Início do Plantão",
          value: "created_at",
          sortable: true,
          align: "start",
          width: "25%",
        },
        {
          text: "Final do Plantão",
          value: "updated_at",
          sortable: true,
          align: "start",
          width: "25%",
        },
        {
          text: "Local de Início",
          value: "init_location",
          sortable: false,
          align: "center",
          width: "25%",
        },
        {
          text: "Local de Término",
          value: "end_location",
          sortable: false,
          align: "center",
          width: "25%",
        },
      ],
    };
  },

  watch: {
    geolocation() {
      this.check_geolocation = this.geolocation == 1;
    },
  },

  created() {
    this.getHistoryDuty();
    this.initialized = true;
  },

  methods: {
    permission,
    formatDate,

    formatTime(datetime) {
      return moment(datetime).format("HH:mm:ss");
    },

    async updateUseGeolocation() {
      try {
        this.loading = true;

        const { user_id, check_geolocation } = this;
        let params = { id: user_id, check_geolocation };

        const response = await api.put(
          "/rest-home/user/use-geolocation",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          this.$emit("save");
          toast(message, "success");
          this.errors = [];
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },

    async getHistoryDuty() {
      try {
        this.loading = true;

        const { user_id, date_init, date_end } = this;
        let params = { user_id, date_init, date_end };

        const response = await api.get("/rest-home/duty-control", {
          params,
        });

        const { data, error, errors } = response.data;

        if (error) {
          this.data = [];
          this.errors = errors;
        } else {
          this.errors = [];
          this.data = data;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.geolocation-settings {
  position: relative;
  background: white;
  min-height: 500px;
}

.settings-content {
  padding: 32px;
}

/* Section Headers */
.config-section,
.history-section {
  margin-bottom: 32px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.section-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Setting Item */
.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.setting-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.setting-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.setting-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.setting-description {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.setting-control {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

/* Modern Switch */
.switch-modern {
  position: relative;
  display: inline-block;
  width: 64px;
  height: 34px;
  cursor: pointer;
}

.switch-modern input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider-modern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  padding: 2px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.slider-thumb {
  width: 30px;
  height: 30px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transform: translateX(0);
}

.thumb-icon {
  font-size: 12px;
  color: #94a3b8;
  transition: all 0.3s ease;
}

.switch-modern input:checked + .slider-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.switch-modern input:checked + .slider-modern .slider-thumb {
  transform: translateX(30px);
}

.switch-modern input:checked + .slider-modern .thumb-icon {
  color: #10b981;
}

.switch-modern input:disabled + .slider-modern {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Status Badge */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.badge-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.badge-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.badge-icon {
  font-size: 10px;
}

/* Filters */
.filters-container {
  background: #f8fafc;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
}

.filters-grid {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 20px;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
}

.date-input-container {
  position: relative;
}

.date-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: white;
  transition: all 0.3s ease;
  padding-right: 40px;
}

.date-input:focus {
  outline: none;
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.date-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.date-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 14px;
}

.filter-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3) !important;
  transition: all 0.3s ease !important;
  position: relative !important;
  min-width: 120px !important;
  height: 48px !important;
}

.filter-btn:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4) !important;
}

.filter-btn:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.btn-spinner {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

/* Table */
.table-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e5e7eb;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.record-count {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  background: rgba(139, 92, 246, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid rgba(139, 92, 246, 0.2);
}

.table-wrapper {
  overflow-x: auto;
}

/* Modern Table Styles */
.modern-table {
  background: white !important;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> thead tr th {
  background: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
  padding: 16px !important;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease !important;
}

.modern-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.modern-table >>> tbody tr td {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 16px !important;
  vertical-align: middle !important;
}

/* Table Cell Styles */
.date-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-icon-container {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  flex-shrink: 0;
}

.start-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.end-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.date-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-primary {
  font-weight: 500;
  color: #1e293b;
  font-size: 13px;
}

.date-secondary {
  font-size: 11px;
  color: #64748b;
}

.location-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 16px;
  z-index: 10;
}

.loading-spinner {
  font-size: 32px;
  color: #8b5cf6;
}

.loading-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .filters-grid {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .filter-button-group {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .settings-content {
    padding: 24px;
  }

  .section-header {
    padding: 16px;
  }

  .setting-item {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .setting-control {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .date-cell {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .date-icon-container {
    width: 24px;
    height: 24px;
    font-size: 9px;
  }
}

@media (max-width: 480px) {
  .settings-content {
    padding: 20px;
  }

  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .header-icon {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }

  .section-title {
    font-size: 16px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .switch-modern {
    width: 56px;
    height: 30px;
  }

  .slider-thumb {
    width: 26px;
    height: 26px;
  }

  .switch-modern input:checked + .slider-modern .slider-thumb {
    transform: translateX(26px);
  }

  .modern-table >>> thead tr th {
    font-size: 11px !important;
    padding: 12px 8px !important;
  }

  .modern-table >>> tbody tr td {
    padding: 12px 8px !important;
  }

  .date-icon-container {
    width: 22px;
    height: 22px;
    font-size: 8px;
  }

  .date-primary {
    font-size: 12px;
  }

  .date-secondary {
    font-size: 10px;
  }
}
</style>